import moment from "moment";
import { useEffect, useState } from "react";

export const numberFormatter = (
  num: number,
  decimal: number = 1,
  unit: string = "",
) => {
  const formatWithCommas = (value: string) => {
    const parts = value.split(".");
    const wholePart = parts[0];
    return wholePart.length >= 4
      ? wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      (parts[1] ? "." + parts[1] : "")
      : value;
  };
  if (num === 0) {
    if (unit === "$") {
      return (unit + "" + 0).trim();
    } else {
      return (0 + " " + unit).trim();
    }
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (num > 999 && num < 1000000) {
    returnValue = formatWithCommas((num / 1000).toFixed(decimal)) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (num >= 1000000 && num < 1000000000) {
    returnValue = formatWithCommas((num / 1000000).toFixed(decimal)) + "M"; // convert to M for number from >= 1 million
  } else if (num >= 1000000000) {
    //convert to B for number from >= 1B
    returnValue = formatWithCommas((num / 1000000000).toFixed(decimal)) + "B";
  } else {
    returnValue = num.toFixed(decimal);
  }
  if (unit === "%") {
    return (
      (isNegative ? `(${returnValue})` : returnValue) +
      " " +
      unit
    ).trim();
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const numberFormatterOnlyInM = (
  num: number,
  decimal: number = 1,
  unit: string = "",
) => {
  const formatWithCommas = (value: string) => {
    const parts = value.split(".");
    const wholePart = parts[0];
    return wholePart.length >= 4
      ? wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      (parts[1] ? "." + parts[1] : "")
      : value;
  };
  if (num === 0) {
    return (unit + 0).trim();
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (num > 999 && num < 1000000) {
    returnValue = formatWithCommas((num / 1000).toFixed(decimal)) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (num >= 1000000) {
    returnValue = formatWithCommas((num / 1000000).toFixed(decimal)) + "M"; // convert to M for number from >= 1 million
  } else {
    returnValue = num.toFixed(decimal);
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const numberFormatterInK = (
  num: number,
  decimal: number = 1,
  unit: string = "",
) => {
  if (num === 0) {
    return (unit + 0).trim();
  }

  const isNegative = num < 0;
  num = Math.abs(num);

  let returnValue = "";

  // Convert to K format and add commas
  returnValue = `${(num / 1000)
    .toFixed(decimal)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}K`;

  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const numberFormatterForKorM = (
  num: number,
  decimal: number = 1,
  unit: string = "",
  KorM: "K" | "M",
) => {
  const formatWithCommas = (value: string) => {
    const parts = value.split(".");
    const wholePart = parts[0];
    return wholePart.length >= 4
      ? wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      (parts[1] ? "." + parts[1] : "")
      : value;
  };
  if (num === 0) {
    return (unit + 0).trim();
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (KorM === "K") {
    returnValue = formatWithCommas((num / 1000).toFixed(decimal)) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (KorM === "M") {
    returnValue = formatWithCommas((num / 1000000).toFixed(decimal)) + "M"; // convert to M for number from >= 1 million
  } else {
    returnValue = num.toFixed(decimal);
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export default function useDebounce(value: any, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const dateFormatterToMMDD = (dateStr: any) => {
  const date = new Date(dateStr);
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}`;
  return formattedDate;
};

export const dateFormatterToMMMYY = (dateStr: any) => {
  const date = new Date(dateStr);
  return date
    .toLocaleDateString("en-us", {
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    })
    .replace(" ", "-");
};


export const dateFormatterToFullMonthShortYear = (dateStr: string, isFullYear = false) => {
  const parsed = moment(dateStr, ["MMM YYYY", "MMMM YYYY"], true);

  if (!parsed.isValid()) {
    return dateStr;
  }

  return isFullYear ? parsed.format("MMMM YYYY") : parsed.format("MMMM[' ]YY");
};

export const getMonthTimestamp = (value: string) => {
  const [month, year] = value.trim().split(" ");
  const monthIndex = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ].indexOf(month);

  if (monthIndex === -1 || !year) {
    return Number.NEGATIVE_INFINITY;
  }

  return new Date(Number(year), monthIndex, 1).getTime();
};

export const sortMonthYearDescending = (dates: string[] = []) => {
  return [...dates].sort((a, b) => {
    const dateA = moment(a, ["MMM YYYY", "MMMM YYYY"], true);
    const dateB = moment(b, ["MMM YYYY", "MMMM YYYY"], true);

    if (!dateA.isValid() && !dateB.isValid()) return 0;
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;

    return dateB.valueOf() - dateA.valueOf();
  });
};

// new formatter: input "2025-09-05T00:00:00.000Z" => "05-Sep-2025"
export const dateFormatterToDDMMMYYYY = (dateStr: any) => {
  if (!dateStr && dateStr !== 0) return "-";
  const m = moment(dateStr);
  if (!m.isValid()) {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    const yy = d.getUTCFullYear();
    return `${dd}-${mon}-${yy}`;
  }
  return m.utc().format("DD-MMM-YYYY");
};

export function truncateTo2Decimals(num: number) {
  return Math.trunc(num * 100) / 100;
}

export const numberFormatterForDecimalInMillion = (
  num: number,
  decimal: number,
  unit: string = "",
) => {
  if (num === 0) {
    return (unit + 0).trim();
  }
  let returnValue = "";
  let isNegative = num < 0;
  num = Math.abs(num);
  if (num > 0 && num < 1) {
    returnValue = (Number((num * 100).toFixed(decimal)) / 100).toFixed(decimal); // if value < 1000, nothing to do
  } else if (num > 999 && num < 100000) {
    returnValue = (num / 1000).toFixed(decimal) + "K"; // convert to K for number from > 1000 < 1 million
  } else if (num >= 100000 && num < 1000000000) {
    returnValue = (num / 1000000).toFixed(decimal) + "M"; // convert to M for number from >= 1 million
  } else if (num >= 1000000000) {
    //convert to B for number from >= 1B
    returnValue = (num / 1000000000).toFixed(decimal) + "B";
  } else {
    returnValue = num.toFixed(decimal);
  }
  return (unit + (isNegative ? `(${returnValue})` : returnValue)).trim();
};

export const formatNumbersToFourDigits = (num: number) => {
  const numStr = num.toString();

  if (numStr.includes(".")) {
    const [integerPart, decimalPart] = numStr.split(".");
    if (integerPart.length >= 4) {
      return parseInt(integerPart.slice(0, 4), 10);
    } else {
      const requiredDecimals = 4 - integerPart.length;
      return parseFloat(
        integerPart + "." + decimalPart.slice(0, requiredDecimals),
      );
    }
  } else {
    return parseInt(numStr.slice(0, 4), 10);
  }
};

export const calculateCommitmentGoalAchieved = (
  start: number,
  final: number,
) => {
  let percentageCalculated = ((final - start) / Math.abs(start)) * 100;

  return parseFloat((100 + percentageCalculated).toFixed(1));
};

export const calculateActualPurchasePace = (start: number, final: number) => {
  if (final === 0) return 0;
  let percentageCalculated = ((start - final) / final) * 100;
  return percentageCalculated;
};

export const formatWithCommas = (value: string) => {
  const parts = value.split(".");
  const wholePart = parts[0];
  return wholePart.length >= 4
    ? wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
    (parts[1] ? "." + parts[1] : "")
    : value;
};

export function excelRound(
  number: number,
  numDigits: number | null | undefined,
) {
  if (numDigits === undefined || numDigits === null) {
    numDigits = 0; // Default to 0 decimal places if not specified
  }

  const multiplier = Math.pow(10, numDigits);
  return Math.round(number * multiplier) / multiplier;
}

export const formatNumberwithCommas = (
  value: any,
  isDecimal = false,
  count = 2,
) => {
  if (value === "-") {
    return value;
  }
  if (value && value?.toString()?.includes(",")[1]) {
    return value;
  }
  const fixed = Number(value).toFixed(isDecimal ? count : 0);
  return fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// export function isMonthInPastOrFuture(dateString: string) {
//   const inputMoment = moment(dateString, 'MMM-YYYY'); // Parse the input string
//   const currentMoment = moment(); // Get the current moment

//   if (!inputMoment.isValid()) {
//     return "Invalid date format.";
//   }

//   // Check if the input month is before the current month
//   if (inputMoment.isBefore(currentMoment, 'month')) {
//     return "Past month";
//   }
//   // Check if the input month is after the current month
//   else if (inputMoment.isAfter(currentMoment, 'month')) {
//     return "Future month";
//   }
//   // If it's neither before nor after, it's the current month
//   else {
//     return "Current month";
//   }
// }
export function isMonthInPastOrFuture(dateString: string) {
  const currentMoment = moment();

  // Handle range: e.g. "Jul 24 to Sep 24"
  if (dateString.includes("to")) {
    const [startStr, endStr] = dateString.split("to").map((s) => s.trim());
    // Parse start and end as "MMM YY"
    const start = moment(startStr, [
      "MMM YY",
      "MMM-YY",
      "MMM YYYY",
      "MMM-YYYY",
    ]);
    const end = moment(endStr, ["MMM YY", "MMM-YY", "MMM YYYY", "MMM-YYYY"]);

    if (!start.isValid() || !end.isValid()) {
      return "Invalid date format.";
    }

    // Check if current month is in the range (inclusive)
    if (
      currentMoment.isSameOrAfter(start, "month") &&
      currentMoment.isSameOrBefore(end, "month")
    ) {
      return "Current month";
    }
    // If current is before range
    if (currentMoment.isBefore(start, "month")) {
      return "Future month";
    }
    // If current is after range
    if (currentMoment.isAfter(end, "month")) {
      return "Past month";
    }
  } else {
    // Single month: e.g. "Jul 25"
    const inputMoment = moment(dateString, [
      "MMM YY",
      "MMM-YY",
      "MMM YYYY",
      "MMM-YYYY",
    ]);
    if (!inputMoment.isValid()) {
      return "Invalid date format.";
    }
    if (inputMoment.isBefore(currentMoment, "month")) {
      return "Past month";
    } else if (inputMoment.isAfter(currentMoment, "month")) {
      return "Future month";
    } else {
      return "Current month";
    }
  }
}

export function extractArrayValuesFromKeys<
  T extends Record<string, any>,
  K extends keyof T,
>(arr: T[] | undefined, key: K): T[K][] {
  return Array.isArray(arr) ? arr.map((elem) => elem[key]) : [];
}

export function sortTreeTableDataByReleaseDate(
  data: any[],
  sortField: string | null,
  sortOrder: number | null,
): any[] {
  if (!sortField || !sortOrder) return data;

  const parseReleaseDate = (val: string): number => {
    if (!val) return 0;
    let first = val;
    if (val.includes("to")) first = val.split("to")[0].trim();
    const monthYearMatch = first.match(/^([A-Za-z]{3})[ -]?(\d{2})$/);
    if (monthYearMatch) {
      const monthStr = monthYearMatch[1].toLowerCase();
      const year = parseInt(monthYearMatch[2], 10);
      const fullYear = year < 100 ? 2000 + year : year;
      const monthMap: { [k: string]: number } = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };
      const month = monthMap[monthStr] || 1;
      return fullYear * 10000 + month * 100 + 1;
    }
    const fullDateMatch = first.match(
      /^([A-Za-z]{3})[ -](\d{1,2})[ -](\d{4})$/,
    );
    if (fullDateMatch) {
      const monthStr = fullDateMatch[1].toLowerCase();
      const day = parseInt(fullDateMatch[2], 10);
      const year = parseInt(fullDateMatch[3], 10);
      const monthMap: { [k: string]: number } = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };
      const month = monthMap[monthStr] || 1;
      return year * 10000 + month * 100 + day;
    }
    const isoDateMatch = first.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})$/);
    if (isoDateMatch) {
      const year = parseInt(isoDateMatch[1], 10);
      const month = parseInt(isoDateMatch[2], 10);
      const day = parseInt(isoDateMatch[3], 10);
      return year * 10000 + month * 100 + day;
    }
    return 0;
  };

  const sortFn = (a: any, b: any) => {
    if (sortField === "releaseDate") {
      let aVal = parseReleaseDate(a.data.releaseDate);
      let bVal = parseReleaseDate(b.data.releaseDate);
      if (aVal == null) return -1 * sortOrder;
      if (bVal == null) return 1 * sortOrder;
      if (aVal < bVal) return -1 * sortOrder;
      if (aVal > bVal) return 1 * sortOrder;
      return 0;
    } else {
      let aVal = a.data[sortField];
      let bVal = b.data[sortField];
      if (aVal == null) return -1 * sortOrder;
      if (bVal == null) return 1 * sortOrder;
      if (aVal < bVal) return -1 * sortOrder;
      if (aVal > bVal) return 1 * sortOrder;
      return 0;
    }
  };

  const sorted = [...data].sort(sortFn);

  return sorted.map((node) => ({
    ...node,
    children:
      node.children && node.children.length > 0
        ? sortTreeTableDataByReleaseDate(
          node.children,
          sortField,
          sortOrder === 1 ? -1 : 1,
        )
        : [],
  }));
}

export function sortTreeTableDataByLastUpdated(
  data: any[],
  sortField: string | null,
  sortOrder: number | null,
): any[] {
  if (!sortField || !sortOrder) return data;

  const parseLastUpdated = (val: string): number => {
    if (!val) return 0;
    const isoMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10);
      const day = parseInt(isoMatch[3], 10);
      return year * 10000 + month * 100 + day;
    }
    const match = val.match(/^([A-Za-z]{3})-(\d{1,2})-(\d{4})$/);
    if (match) {
      const monthStr = match[1].toLowerCase();
      const day = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      const monthMap: { [k: string]: number } = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };
      const month = monthMap[monthStr] || 1;
      return year * 10000 + month * 100 + day;
    }
    return 0;
  };

  const sortFn = (a: any, b: any) => {
    if (sortField === "lastUpdated") {
      let aVal = parseLastUpdated(a.data.lastUpdated);
      let bVal = parseLastUpdated(b.data.lastUpdated);
      if (aVal == null) return -1 * sortOrder;
      if (bVal == null) return 1 * sortOrder;
      if (aVal < bVal) return -1 * sortOrder;
      if (aVal > bVal) return 1 * sortOrder;
      return 0;
    } else {
      let aVal = a.data[sortField];
      let bVal = b.data[sortField];
      if (aVal == null) return -1 * sortOrder;
      if (bVal == null) return 1 * sortOrder;
      if (aVal < bVal) return -1 * sortOrder;
      if (aVal > bVal) return 1 * sortOrder;
      return 0;
    }
  };

  const sorted = [...data].sort(sortFn);

  return sorted.map((node) => ({
    ...node,
    children:
      node.children && node.children.length > 0
        ? sortTreeTableDataByLastUpdated(node.children, sortField, sortOrder)
        : [],
  }));
}

export const getRemainingVolume = (item: any) => {
  const committed = item?.Total_Commited_Volume;
  const actual = item?.Total_Actual_Volume;

  // Case 1: If committed is nullish, remaining is 0
  if (committed == null) return 0;

  // Case 2: Actual is nullish, take full committed
  if (actual == null) return Number(committed);

  // Case 3: Both present, subtract (but not less than zero)
  const diff = committed - actual;
  return diff < 0 ? 0 : Number(diff.toFixed(1));
};

export const numberFormatterForDecimalInMNew = (
  num: number,
  decimal: number,
  unit: string = ""
) => {
  if (num === 0) {
    return (unit + 0).trim();
  }

  const isNegative = num < 0;
  let abs = Math.abs(num);
  let divisor = 1;
  let suffix = "";

  const steps = [
    { threshold: 1_000_000_000, label: "B" },
    { threshold: 1_000_000, label: "M" },
    { threshold: 1_000, label: "K" },
  ];

  for (const step of steps) {
    if (abs >= step.threshold) {
      divisor = step.threshold;
      suffix = step.label;
      break;
    }
  }

  const pow = Math.pow(10, decimal);
  const scaled = Math.trunc((abs / divisor) * pow) / pow;

  const formatted =
    suffix === ""
      ? formatWithCommas(abs.toFixed(decimal))
      : formatWithCommas(scaled.toFixed(decimal)) + suffix;

  return (unit + (isNegative ? `(${formatted})` : formatted)).trim();
};